import React, { useState, useEffect, useRef, ChangeEvent, FC } from 'react';
import useDidUpdate from '../../../../../../hooks/useDidUpdate.effect';
import { documentService } from '../../../../../../utils/window.utils.js';
import Icon from '../../../../../@shared/icon';
import HiglightedOption from '../../../../../@shared/highlightedOption';
import DictionaryTerm from '../../../../../@shared/dictionaryTerm';
import { TTimeFieldDropdown } from '../../../../types/timeField.types';
import { iconSize } from '../../../../../../constants/icon.constants';

const HoursMinutesDropDown: FC<TTimeFieldDropdown> = ({
  dropdownOptions = [],
  id,
  handleOptionClick,
  fields,
  label,
  defaultValue,
  hourMinutesFormat,
}) => {
  const { field, tracker } = fields;
  const hasAnyError = !!fields?.errors?.length;
  const [isVisible, setIsVisible] = useState(false);
  const [hasFocus, setHasFocus] = useState(false);
  const [hasTextInputFocus, setHasTextInputFocus] = useState(false);
  const [initialTextValue, setInitialTextValue] = useState('');
  const [textValue, setTextValue] = useState('');
  const dropdownRef: React.MutableRefObject<HTMLUListElement | null> = useRef(null);
  const inputRef: React.MutableRefObject<HTMLInputElement | null> = useRef(null);

  useEffect(() => {
    if (!fields.value) {
      setTextValue(defaultValue);
      setInitialTextValue(defaultValue);
    }
  }, [fields.value]);

  const onDropdownFocus = (): void => {
    setHasFocus(true);
    setHasTextInputFocus(true);
  };

  const onTextInputBlur = (): void => {
    setHasTextInputFocus(false);
  };

  const onDropdownClick = (): void => {
    setIsVisible(!isVisible);

    if (!isVisible) {
      inputRef?.current?.focus();
    } else {
      inputRef?.current?.blur();
    }
  };

  const onSelectOption = (value, text): void => {
    setTextValue(text);
    setInitialTextValue(value);
    setIsVisible(false);
  };

  const onInputUpdate = (e: ChangeEvent<HTMLInputElement>): void => {
    setTextValue(e?.target?.value);
  };

  const handleClickOutside = (event): void => {
    if (dropdownRef?.current?.contains(event?.target)) {
      return;
    }
    setIsVisible(false);

    const hasMatch = dropdownOptions.some((opt) => opt.value === textValue);

    if (!hasMatch) {
      setTextValue(initialTextValue);
    } else {
      handleOptionClick(textValue);
      setInitialTextValue(textValue);
    }

    setHasFocus(false);
  };

  useEffect(() => {
    documentService()?.addEventListener('mouseup', handleClickOutside);
    return (): void => {
      documentService()?.removeEventListener('mouseup', handleClickOutside);
    };
  }, [initialTextValue, textValue]); // eslint-disable-line

  useDidUpdate(() => {
    if (hasFocus) {
      tracker?.onFocusField(field, textValue);
    } else {
      tracker?.onBlurField(field, textValue);
    }
  }, [hasFocus]);

  return (
    <ul className={`form-custom-dropdown ${hasAnyError ? 'form-custom-dropdown--has-error' : ''}`} ref={dropdownRef}>
      <li
        className={`form-custom-dropdown__input-container ${hasFocus ? 'form-custom-dropdown__input-container--has-focus' : ''}
        ${hourMinutesFormat ? 'form-custom-dropdown--time' : ''}  `}
      >
        {hourMinutesFormat && (
          <Icon iconName="time-clock" size={iconSize.dsk} color="gray" extraClassName="form-custom-dropdown--time-icon" />
        )}
        <label
          htmlFor={id}
          className={`form-custom-dropdown__label ${
            !hasTextInputFocus && textValue === '' ? 'form-custom-dropdown__label--empty-field' : ''
          }`}
        >
          <DictionaryTerm keyword={label} />
        </label>
        <input
          className="form-custom-dropdown__text-input"
          type="text"
          value={textValue}
          onChange={onInputUpdate}
          ref={inputRef}
          id={id}
          onClick={onDropdownClick}
          onFocus={onDropdownFocus}
          onBlur={onTextInputBlur}
          autoComplete="off"
          form="novalidatedform"
        />
        <Icon
          iconName={isVisible ? 'arrow-chevron-up' : 'arrow-chevron-down'}
          size={iconSize.dsk}
          extraClassName="form-custom-dropdown__icon"
          color="gray"
          onClick={onDropdownClick}
        />
      </li>

      <li className={`form-custom-dropdown__list-container ${isVisible ? 'form-custom-dropdown__list-container--visible' : ''}`}>
        <ul className="form-custom-dropdown__list" role="listbox">
          {dropdownOptions.map((item) => {
            return (
              <li
                className="form-custom-dropdown__list-item"
                role="option" // eslint-disable-line
                onClick={(): void => {
                  onSelectOption(item.value || '', item.text || '');
                  handleOptionClick(item.value);
                }}
                key={item.value}
              >
                <HiglightedOption keyword={textValue} optionText={item.text} key={item.value} />
              </li>
            );
          })}
        </ul>
      </li>
    </ul>
  );
};

export default HoursMinutesDropDown;
